import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  GetApp as DownloadIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { t } from '../../utils/textUtils';

const ImportDialog = ({ open, onClose, projectId, onImportSuccess, embedded = false }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const steps = [
    t('DOWNLOAD TEMPLATE'),
    t('UPLOAD FILE'),
    t('HASIL IMPORT')
  ];

  const handleClose = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setUploading(false);
    setImportResult(null);
    setErrors([]);
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/import/template`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `project_${projectId}_import_template.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('Template berhasil didownload', { variant: 'success' });
      setActiveStep(1);
    } catch (error) {
      console.error('Error downloading template:', error);
      enqueueSnackbar('Gagal mendownload template', { variant: 'error' });
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        enqueueSnackbar('Format file tidak didukung. Gunakan Excel (.xlsx) atau CSV', { variant: 'error' });
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      enqueueSnackbar('Pilih file terlebih dahulu', { variant: 'warning' });
      return;
    }

    setUploading(true);
    setErrors([]);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post(`/projects/${projectId}/import/structure`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setImportResult(response.data);
        setActiveStep(2);
        enqueueSnackbar('Import berhasil!', { variant: 'success' });
        
        // Call success callback
        if (onImportSuccess) {
          onImportSuccess(response.data);
        }
      } else {
        setErrors(response.data.errors || ['Import gagal']);
        enqueueSnackbar('Import gagal', { variant: 'error' });
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error.response?.data?.message || 'Terjadi kesalahan saat import';
      const errorDetails = error.response?.data?.errors || [];
      
      setErrors([errorMessage, ...errorDetails]);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" gutterBottom>{t('DOWNLOAD TEMPLATE IMPORT')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('DOWNLOAD TEMPLATE EXCEL YANG SUDAH BERISI FORMAT YANG BENAR UNTUK IMPORT DATA PROYEK')}
            </Typography>
            
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('TEMPLATE BERISI 4 SHEET:')}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="info" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('UNIT MODULAR')} 
                    secondary={t('UNIT MODULAR ATAU AREA KERJA')} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="info" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('KATEGORI PEKERJAAN')} 
                    secondary={t('KATEGORI PEKERJAAN (CIVIL, ELECTRICAL, DLL)')} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="info" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('DAFTAR PEKERJAAN')} 
                    secondary={t('DAFTAR PEKERJAAN DENGAN DEPENDENCIES')} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="info" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="LIST SUBCON" 
                    secondary="DAFTAR SUBKONTRAKTOR DARI DATABASE" 
                  />
                </ListItem>
              </List>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                size="large"
              >
                {t('DOWNLOAD TEMPLATE')}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => setActiveStep(1)}
                size="large"
                color="secondary"
              >
                {t('UPLOAD LANGSUNG')}
              </Button>
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              {t('SUDAH PUNYA FILE? KLIK "UPLOAD LANGSUNG" UNTUK MELEWATI DOWNLOAD TEMPLATE')}
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" gutterBottom>{t('UPLOAD FILE IMPORT')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('PILIH FILE EXCEL YANG SUDAH DIISI SESUAI TEMPLATE')}
            </Typography>

            <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 3, textAlign: 'center', mb: 3 }}>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  size="large"
                >
                  {t('PILIH FILE')}
                </Button>
              </label>
              
              {selectedFile && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    {t('FILE TERPILIH')}: <strong>{selectedFile.name}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('UKURAN')}: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
              )}
            </Box>

            {errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('ERROR SAAT IMPORT:')}
                </Typography>
                <List dense>
                  {errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={error} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                startIcon={uploading ? <LinearProgress size={20} /> : <UploadIcon />}
              >
                {uploading ? t('MENGUPLOAD...') : t('UPLOAD & IMPORT')}
              </Button>
            </Box>

            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                  {t('SEDANG MEMPROSES FILE...')}
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" gutterBottom>{t('HASIL IMPORT')}</Typography>
            
            {importResult && (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('IMPORT BERHASIL!')}
                  </Typography>
                  <Typography variant="body2">
                    {importResult.summary || t('DATA BERHASIL DIIMPORT KE PROYEK')}
                  </Typography>
                </Alert>

                {importResult.details && (
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('DETAIL IMPORT:')}
                    </Typography>
                    <List dense>
                      {importResult.details.workCategories && (
                        <ListItem>
                          <ListItemIcon>
                            <CheckIcon color="success" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={`${importResult.details.workCategories} ${t('KATEGORI PEKERJAAN')}`}
                          />
                        </ListItem>
                      )}
                      {importResult.details.modularUnits && (
                        <ListItem>
                          <ListItemIcon>
                            <CheckIcon color="success" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={`${importResult.details.modularUnits} ${t('UNIT MODULAR')}`}
                          />
                        </ListItem>
                      )}
                      {importResult.details.tasks && (
                        <ListItem>
                          <ListItemIcon>
                            <CheckIcon color="success" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={`${importResult.details.tasks} ${t('DAFTAR PEKERJAAN')}`}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  // Render content
  const renderContent = () => (
    <>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Divider sx={{ mb: 2 }} />

      {renderStepContent(activeStep)}
      
      {!embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
          <Button onClick={handleClose}>
            {activeStep === 2 ? t('SELESAI') : t('BATAL')}
          </Button>
          
          {activeStep === 1 && (
            <Button 
              onClick={() => setActiveStep(0)}
              disabled={uploading}
            >
              {t('KEMBALI')}
            </Button>
          )}
        </Box>
      )}
    </>
  );

  // If embedded mode, return content without dialog wrapper
  if (embedded) {
    return (
      <Paper sx={{ p: 3 }}>
        {renderContent()}
      </Paper>
    );
  }

  // Regular dialog mode
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{t('IMPORT STRUKTUR PROYEK')}</Typography>
          <Tooltip title={t('TUTUP')}>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;